import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleMinus, faUserMinus, faUserPlus } from '@fortawesome/pro-solid-svg-icons'
import { AuthOption } from '../../../../shared/sso-workspace/auth-card'
import { AccountAvatar, AccountSub, AccountTitle } from '../../../../shared/recent-accounts/account-view'
import { emailKey } from '../../../../shared/recent-accounts/recent-accounts'

// "Choose an account" (owner 2026-10-25), the sign-in page's first view when
// this browser remembers accounts (shared/recent-accounts). login-email.js
// owns the list and what a choice does; this draws it.
//
// One list, drawn like Google's chooser (owner 2026-10-29: separate boxes
// made the action rows look like accounts with missing details): the rows
// share one border with a line between them and no chevrons. One row per
// account (the shared AuthOption: avatar or initials, name, email, the last
// method as a hint), then two plainer action rows, a bare icon and regular
// text, "Use another account" and, last, "Remove an account" (owner
// 2026-10-28). Choosing an account row is the same as typing that email
// (onChoose): the server still decides SSO, password or the connection
// chooser.
//
// Remove mode (the "Remove an account" row turns it on; login-email.js owns
// the flag and the heading, which then reads "Remove an account" with one
// plain line of help): each row has a red minus-circle Remove button (44px,
// circular hover and focus), "Forget all accounts on this device" is a quiet
// text action, and a "Done" button ends it. Removing only forgets the account
// in this browser's list; it never signs anyone out or deletes anything.
//
// Keyboard and screen readers: rows and every control are buttons in the tab
// order; the row being looked up keeps focus (the others wait, disabled) and
// "Checking <email>…" is announced; a removal is announced and moves focus to
// the next row's Remove (or the one before, or Done). Entering remove mode
// focuses the first Remove; Done returns focus to the "Remove an account"
// row. The page moves to the email field itself when the list empties.
export const AccountChooser = ({ accounts, editing, onEditingChange, busyEmail, onChoose, onUseAnother, onRemove, onForgetAll, labelledBy }) => {
  const removeRefs = useRef({})
  const doneRef = useRef(null)
  const removeModeRef = useRef(null)
  const wasEditing = useRef(editing)
  const [focusIndex, updateFocusIndex] = useState(null)
  const [announcement, updateAnnouncement] = useState('')

  useEffect(
    () => {
      if (busyEmail) updateAnnouncement(`Checking ${busyEmail}…`)
    },
    [busyEmail]
  )

  useEffect(
    () => {
      if (focusIndex === null) return
      const target = accounts[Math.min(focusIndex, accounts.length - 1)]
      const element = target && removeRefs.current[emailKey(target.email)]
      if (element) element.focus()
      else if (doneRef.current) doneRef.current.focus()
      updateFocusIndex(null)
    },
    [focusIndex, accounts]
  )

  // Entering remove mode lands on the first Remove; leaving it lands back on
  // the row that opened it.
  useEffect(
    () => {
      if (editing === wasEditing.current) return
      wasEditing.current = editing
      if (editing) {
        const first = accounts[0] && removeRefs.current[emailKey(accounts[0].email)]
        if (first) first.focus()
      } else if (removeModeRef.current) removeModeRef.current.focus()
    },
    [editing, accounts]
  )

  const handleRemove = (account, index) => {
    onRemove(account)
    updateAnnouncement(`${account.email} won't be suggested on this device.`)
    updateFocusIndex(index)
  }

  const handleToggle = () => {
    updateAnnouncement('')
    onEditingChange(!editing)
  }

  const busy = !!busyEmail

  return (
    <div className="sa-accounts" data-testid="account-chooser" data-editing={editing ? 'true' : 'false'}>
      <ul className="sa-options sa-account-list" aria-labelledby={labelledBy}>
        {accounts.map((account, index) =>
          <li key={emailKey(account.email)}>
            {editing
              ? <div className="sa-option sa-account-edit">
                  <AccountAvatar account={account} />
                  <span className="sa-option-name">
                    <span className="sa-option-title">
                      <AccountTitle account={account} />
                    </span>
                    <span className="sa-option-sub">
                      <AccountSub account={account} />
                    </span>
                  </span>
                  <button
                    type="button"
                    ref={element => {
                      removeRefs.current[emailKey(account.email)] = element
                    }}
                    className="sa-account-remove"
                    aria-label={`Remove ${account.name ? `${account.name} (${account.email})` : account.email} from this device`}
                    onClick={() => handleRemove(account, index)}>
                    <FontAwesomeIcon icon={faCircleMinus} />
                  </button>
                </div>
              : <AuthOption
                  logo={<AccountAvatar account={account} />}
                  name={<AccountTitle account={account} />}
                  sub={<AccountSub account={account} />}
                  disabled={busy && busyEmail !== account.email}
                  action={busyEmail === account.email
                    ? <span className="sa-spin" aria-hidden="true" data-testid="account-checking" />
                    : undefined}
                  onClick={() => {
                    if (!busy) onChoose(account)
                  }}
                />}
          </li>
        )}

        {!editing &&
          <li>
            <button type="button" className="sa-option sa-option-quiet" disabled={busy} onClick={onUseAnother}>
              <span className="sa-quiet-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faUserPlus} />
              </span>
              <span className="sa-option-name">Use another account</span>
            </button>
          </li>}

        {!editing &&
          <li>
            <button type="button" ref={removeModeRef} className="sa-option sa-option-quiet" disabled={busy} onClick={handleToggle}>
              <span className="sa-quiet-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faUserMinus} />
              </span>
              <span className="sa-option-name">Remove an account</span>
            </button>
          </li>}
      </ul>


      {editing &&
        <div className="sa-remove-actions">
          <button type="button" ref={doneRef} className="btn sw-btn-primary sa-block" onClick={handleToggle}>
            Done
          </button>
          <button type="button" className="sa-forget-all" onClick={onForgetAll}>
            Forget all accounts on this device
          </button>
        </div>}

      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
    </div>
  )
}
